import { useState } from "react";
import { PackageSearch, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Reveal } from "../components/store/Reveal";

const STEPS = ["pagato", "in preparazione", "spedito", "consegnato"];

export default function TrackOrder() {
  const [form, setForm] = useState({ order_id: "", email: "" });
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setOrder(null);
    try {
      const r = await api.get("/orders/track", { params: form });
      setOrder(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ordine non trovato");
    } finally {
      setLoading(false);
    }
  };

  const stepIndex = order ? STEPS.indexOf(order.status) : -1;

  return (
    <div data-testid="track-order-page" className="max-w-2xl mx-auto px-4 sm:px-8 py-14 lg:py-20">
      <Reveal>
        <div className="w-12 h-12 rounded-full bg-[#2E8A57]/10 flex items-center justify-center mb-6">
          <PackageSearch className="w-5 h-5 text-[#2E8A57]" />
        </div>
        <h1 className="font-serif-display font-bold text-3xl sm:text-4xl tracking-tight text-[#1C1917]">Traccia il tuo ordine</h1>
        <p className="mt-3 text-[#44403C]">Inserisci il numero ordine (lo trovi nell'email di conferma) e la tua email.</p>
      </Reveal>

      <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-4">
        <input
          data-testid="track-order-id-input"
          required
          placeholder="Numero ordine (es. 14ff1e64)"
          value={form.order_id}
          onChange={(e) => setForm({ ...form, order_id: e.target.value })}
          className="flex-1 rounded-xl border border-[#E7E0D8] bg-white px-4 py-3.5 text-sm focus:outline-none focus:border-[#2E8A57]"
        />
        <input
          data-testid="track-email-input"
          required
          type="email"
          placeholder="Email dell'ordine"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="flex-1 rounded-xl border border-[#E7E0D8] bg-white px-4 py-3.5 text-sm focus:outline-none focus:border-[#2E8A57]"
        />
        <button data-testid="track-submit-btn" type="submit" disabled={loading} className="btn-tuscan px-8 py-3.5 rounded-full text-sm font-semibold disabled:opacity-40">
          {loading ? "Ricerca..." : "Cerca"}
        </button>
      </form>

      {order && (
        <div data-testid="track-result" className="mt-10 rounded-2xl border border-[#E7E0D8] bg-white p-6 lg:p-8">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="font-mono-accent text-xs text-[#78716C]">ORDINE #{order.id.slice(0, 8)} — {new Date(order.created_at).toLocaleDateString("it-IT")}</p>
            <span data-testid="track-status" className="rounded-full bg-[#2E8A57] text-[#FDFBF7] px-4 py-1.5 text-xs font-semibold uppercase tracking-wide">{order.status}</span>
          </div>

          {stepIndex >= 0 && (
            <div className="mt-7 flex items-center">
              {STEPS.map((s, i) => (
                <div key={s} className="flex-1 flex flex-col items-center relative">
                  {i > 0 && <span className={`absolute top-3 right-1/2 w-full h-0.5 ${i <= stepIndex ? "bg-[#3C6E53]" : "bg-[#E7E0D8]"}`} />}
                  <span className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center ${i <= stepIndex ? "bg-[#3C6E53] text-white" : "bg-[#E7E0D8]"}`}>
                    {i <= stepIndex && <CheckCircle2 className="w-4 h-4" />}
                  </span>
                  <span className={`mt-2 text-[10px] font-mono-accent uppercase tracking-wide text-center ${i <= stepIndex ? "text-[#2D4A3E] font-semibold" : "text-[#78716C]"}`}>{s}</span>
                </div>
              ))}
            </div>
          )}

          {order.tracking_number && (
            <div data-testid="track-tracking-block" className="mt-5 rounded-xl bg-[#F3F0E9] px-5 py-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm">
                <span className="font-semibold">Tracking{order.tracking_carrier ? ` ${order.tracking_carrier}` : ""}:</span>{" "}
                <span className="font-mono-accent">{order.tracking_number}</span>
              </p>
              {order.tracking_url && (
                <a data-testid="track-carrier-link" href={order.tracking_url} target="_blank" rel="noreferrer" className="btn-tuscan px-5 py-2 rounded-full text-xs font-semibold">
                  Segui la spedizione
                </a>
              )}
            </div>
          )}

          <div className="mt-7 border-t border-[#E7E0D8] pt-5 space-y-1.5">
            {order.items.map((i) => (
              <div key={i.slug} className="flex justify-between text-sm">
                <span>{i.name} × {i.quantity}</span>
                <span className="font-mono-accent">{(i.price * i.quantity).toFixed(2).replace(".", ",")} €</span>
              </div>
            ))}
            <div className="flex justify-between pt-3 font-semibold">
              <span>Totale</span>
              <span className="font-serif-display text-lg">{order.total.toFixed(2).replace(".", ",")} €</span>
            </div>
          </div>
          {order.delivery_date && (
            <p className="mt-4 text-sm text-[#3C6E53] font-semibold">Consegna richiesta: {new Date(order.delivery_date).toLocaleDateString("it-IT")} — {order.city}</p>
          )}
        </div>
      )}
    </div>
  );
}
